import { useCallback, useState } from "react"
import { useMutation } from "@tanstack/react-query"
import { useAuthedAxios } from "hooks/core/useAuthedAxios"
import {
  checkQuestionnaire,
  getQuestionnaire,
  saveQuestionnaire,
  getQuestionnaireTranslations,
  saveQuestionnaireTranslations,
} from "api/Repos/questionnaire"
import {
  QuestionnaireGetResponse,
  QuestionnaireSaveResponse,
  QuestionnaireCheckResponse,
  QuestionnaireTranslationsGetResponse,
  QuestionnaireTranslationsSaveResponse,
  Translation,
} from "types/questionnaire.types"

/**
 * Questionnaire document operations. Imperative by design: the editor owns
 * the document state (with Yjs collaboration on top), so there is no query
 * cache — reads are plain authed calls, saves run as react-query mutations.
 */
interface UseQuestionnaireReturn {
  getQuestionnaireByID: (
    id: string,
    owner: string
  ) => Promise<QuestionnaireGetResponse>
  isLoadingQuestionnaire: boolean

  /** Background fetch (autosave flows) — no loading state. */
  getQuestionnaireSilent: (
    id: string,
    owner: string
  ) => Promise<QuestionnaireGetResponse>

  saveQuestionnaireByID: (
    id: string,
    qnr: string,
    owner: string
  ) => Promise<QuestionnaireSaveResponse>
  isSavingQuestionnaire: boolean

  checkQuestionnaireByID: (id: string) => Promise<QuestionnaireCheckResponse>
  isCheckingQuestionnaire: boolean

  getQuestionnaireTranslationsByID: (
    id: string,
    owner: string
  ) => Promise<QuestionnaireTranslationsGetResponse>
  isGettingQuestionnaireTranslations: boolean

  saveQuestionnaireTranslationsByID: (
    id: string,
    owner: string,
    translations: Translation[]
  ) => Promise<QuestionnaireTranslationsSaveResponse>
  isSavingQuestionnaireTranslations: boolean
}

export const useQuestionnaire = (): UseQuestionnaireReturn => {
  const getAxios = useAuthedAxios()

  const [isLoadingQuestionnaire, setIsLoadingQuestionnaire] = useState(false)
  const [isCheckingQuestionnaire, setIsCheckingQuestionnaire] = useState(false)
  const [
    isGettingQuestionnaireTranslations,
    setIsGettingQuestionnaireTranslations,
  ] = useState(false)

  const getQuestionnaireSilent = useCallback(
    async (id: string, owner: string): Promise<QuestionnaireGetResponse> =>
      getQuestionnaire(await getAxios(), id, owner),
    [getAxios]
  )

  const getQuestionnaireByID = useCallback(
    async (id: string, owner: string): Promise<QuestionnaireGetResponse> => {
      setIsLoadingQuestionnaire(true)
      try {
        return await getQuestionnaireSilent(id, owner)
      } finally {
        setIsLoadingQuestionnaire(false)
      }
    },
    [getQuestionnaireSilent]
  )

  const checkQuestionnaireByID = useCallback(
    async (id: string): Promise<QuestionnaireCheckResponse> => {
      setIsCheckingQuestionnaire(true)
      try {
        return await checkQuestionnaire(await getAxios(), id)
      } finally {
        setIsCheckingQuestionnaire(false)
      }
    },
    [getAxios]
  )

  const getQuestionnaireTranslationsByID = useCallback(
    async (
      id: string,
      owner: string
    ): Promise<QuestionnaireTranslationsGetResponse> => {
      setIsGettingQuestionnaireTranslations(true)
      try {
        return await getQuestionnaireTranslations(await getAxios(), id, owner)
      } finally {
        setIsGettingQuestionnaireTranslations(false)
      }
    },
    [getAxios]
  )

  const saveMutation = useMutation({
    mutationFn: async (args: [string, string, string]) =>
      saveQuestionnaire(await getAxios(), ...args),
  })

  const saveTranslationsMutation = useMutation({
    mutationFn: async (args: [string, string, Translation[]]) =>
      saveQuestionnaireTranslations(await getAxios(), ...args),
  })

  const saveQuestionnaireByID = useCallback(
    (id: string, qnr: string, owner: string) =>
      saveMutation.mutateAsync([id, qnr, owner]),
    [saveMutation.mutateAsync]
  )

  const saveQuestionnaireTranslationsByID = useCallback(
    (id: string, owner: string, translations: Translation[]) =>
      saveTranslationsMutation.mutateAsync([id, owner, translations]),
    [saveTranslationsMutation.mutateAsync]
  )

  return {
    getQuestionnaireByID,
    isLoadingQuestionnaire,
    getQuestionnaireSilent,
    saveQuestionnaireByID,
    isSavingQuestionnaire: saveMutation.isPending,
    checkQuestionnaireByID,
    isCheckingQuestionnaire,
    getQuestionnaireTranslationsByID,
    isGettingQuestionnaireTranslations,
    saveQuestionnaireTranslationsByID,
    isSavingQuestionnaireTranslations: saveTranslationsMutation.isPending,
  }
}
